import axios, { AxiosInstance } from 'axios';
import InvalidCredentialsException from '../exceptions/InvalidCredentialsException';
import FailedEncryptionException from '../exceptions/FailedEncryptionException';

import RequestBehaviour from './RequestBehaviour';

import Response from './Response';
import ErrorResponse from '../responses/ErrorResponse';
import FailureResponse from '../responses/FailureResponse';
import InputErrorResponse from '../responses/InputErrorResponse';
import NetworkFailureResponse from '../responses/NetworkFailureResponse';
import NoAccessResponse from '../responses/NoAccessResponse';
import NotFoundResponse from '../responses/NotFoundResponse';
import PayloadResponse from '../responses/PayloadResponse';
import PaymentDeclinedResponse from '../responses/PaymentDeclinedResponse';
import PreconditionalResponse from '../responses/PreconditionalResponse';
import SuccessResponse from '../responses/SuccessResponse';
import Settings from '../models/Settings';
import TimeoutResponse from '../responses/TimeoutResponse';

const PUBLIC_KEY_PATH = 'api/v2/merchant/public_key';

export default class ServiceBehaviour {
	/**
	 * Settings service model
	 */
	settings: Settings;

	/**
	 * Public key from pair generated by current instance.
	 */
	private sdk_public_key?: string | null;

	/**
	 * Initialize service
	 */
	constructor(settings: Settings) {
		this.settings = settings;
	}

	/**
	 * Build HTTP request to API
	 *
	 * @param url
	 * @return
	 * @throws InvalidCredentialsException
	 */
	private buildRequest(url: string, transaction: RequestBehaviour): AxiosInstance {
		if (!this.settings.auth_key || !this.settings.auth_hash) {
			throw new InvalidCredentialsException('The merchant credentials are not definied (key/hash).');
		}

		if (this.settings.environment != null) {
			transaction.env = this.settings.environment;
		}

		if (this.settings.lang != null) {
			transaction.lang = this.settings.lang;
		}

		if (this.settings.sdk != null) {
			transaction.from = this.settings.sdk;
		}

		if (this.settings.sdk_version != null) {
			transaction.sdk_version = this.settings.sdk_version;
		}

		const headers: Record<string, string | undefined | null> = {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
			'x-auth-key': this.settings.auth_key,
			'x-auth-hash': this.settings.auth_hash,
		};

		if (this.settings.auth_user) {
			headers['x-auth-user'] = this.settings.auth_user;
		}

		if (this.sdk_public_key) {
			headers['x-auth-secure'] = this.sdk_public_key;
		}

		if (this.settings.headers) {
			for (const header in this.settings.headers) {
				headers[header] = this.settings.headers[header];
			}
		}

		return axios.create({
			baseURL: this.settings.endpoint,
			timeout: 1000 * 60,
			headers,
			validateStatus: () => true,
		});
	}

	/**
	 * Mapping and cast HTTP response
	 *
	 * @param body
	 * @param status
	 * @return
	 */
	parseResponse(data: any, status: number): Response {
		let bag = new Response();

		switch (status) {
			case 200: bag = new SuccessResponse(); break;

			case 202: bag = new PayloadResponse(); break;

			case 400: bag = new ErrorResponse(); break;

			case 401:
			case 403: bag = new NoAccessResponse(); break;

			case 402: bag = new PaymentDeclinedResponse(); break;

			case 404:
			case 405:
			case 406: bag = new NotFoundResponse(); break;

			case 408: bag = new TimeoutResponse(); break;

			case 412:
			case 418: bag = new PreconditionalResponse(); break;

			case 422: bag = new InputErrorResponse(); break;

			case 500: bag = new FailureResponse(); break;

			default:
				if (status > 500) {
					bag = new NetworkFailureResponse();
				}
				break;
		}

		bag.status = status;
		bag.action = data?.action || null;
		bag.success = data?.success || false;
		bag.message = data?.message || null;
		bag.data = data?.data || null;
		bag.errors = data?.errors || null;

		return bag;
	}

	/**
	 * Default response for exception behavior;
	 *
	 * @param e
	 * @returns
	 */
	private exceptionResponse(e: Error): Response {
		const response = new FailureResponse();
		response.status = 520;
		response.success = false;
		response.message = e.message;

		return response;
	}

	/**
	 * Verify and set request encryption values
	 *
	 * @param request
	 */
	private async validateEncryption(request: RequestBehaviour) {
		if (!request.isEncryptable()) {
			return;
		}

		await this.validateMerchantPublicKey();
		this.validateSDKPublicKey(request);
	}

	/**
	 * Resiliently fetch merchant public key, trying at least 3 times before failing
	 *
	 * @throws InvalidCredentialsException
	 */
	private async validateMerchantPublicKey() {
		let tries: number = 0;

		while (!this.settings.public_key && tries < 3) {
			this.settings.public_key = await this.fetchMerchantPublicKey();
			tries++;
		}

		if (this.settings.public_key == null) {
			throw new InvalidCredentialsException('Could not obtain necessary credentials for transaction.');
		}
	}

	/**
	 * Resiliently generate keys and encrypt request, trying at least 3 times before failing
	 *
	 * @param request
	 * @throws FailedEncryptionException
	 */
	private validateSDKPublicKey(request: RequestBehaviour) {
		let tries: number = 0;
		let lastError: FailedEncryptionException | null = null;

		while (!this.sdk_public_key && tries < 3 || tries === 0) {
			try {
				this.sdk_public_key = request.withEncryption(this.settings.public_key);
			} catch (err) {
				lastError = err as FailedEncryptionException;
			} finally {
				tries++;
			}
		}

		if (!this.sdk_public_key && lastError) {
			throw lastError;
		}

		if (!this.sdk_public_key) {
			throw new FailedEncryptionException('Could not process encryption, please try again.');
		}
	}

	/**
	 * Fetch merchant public key for encryption
	 *
	 * @return
	 * @throws InvalidCredentialsException
	 */
	private async fetchMerchantPublicKey(): Promise<string | null> {
		const response = await this.get(PUBLIC_KEY_PATH, new RequestBehaviour());

		if (response.success) {
			return response.getData('public_key');
		}

		return null;
	}

	/**
	 * Get API route
	 *
	 * @param route
	 * @return
	 */
	protected getRoute(route: string): string {
		return this.settings.endpoint + '/' + route;
	}

	/**
	 * API POST request
	 *
	 * @param url
	 * @param body
	 * @return
	 * @throws InvalidCredentialsException
	 */
	protected async post(url: string, body: RequestBehaviour): Promise<Response> {
		try {
			await this.validateEncryption(body);

			const response = await this.buildRequest(url, body).post(url, JSON.parse(body.toJson()));

			return this.parseResponse(response.data, response.status);
		} catch (e) {
			return this.exceptionResponse(e as Error);
		}
	}

	/**
	 * API PUT request
	 *
	 * @param url
	 * @param body
	 * @return
	 * @throws InvalidCredentialsException
	 */
	protected async put(url: string, body: RequestBehaviour): Promise<Response> {
		try {
			await this.validateEncryption(body);

			const response = await this.buildRequest(url, body).put(url, JSON.parse(body.toJson()));

			return this.parseResponse(response.data, response.status);
		} catch (e) {
			return this.exceptionResponse(e as Error);
		}
	}

	/**
	 * API DELETE request
	 *
	 * @param url
	 * @param body
	 * @return
	 * @throws InvalidCredentialsException
	 */
	protected async delete(url: string, body: RequestBehaviour): Promise<Response> {
		try {
			await this.validateEncryption(body);

			const response = await this.buildRequest(url, body).delete(url, {
				params: JSON.parse(body.toJson()),
			});

			return this.parseResponse(response.data, response.status);
		} catch (e) {
			return this.exceptionResponse(e as Error);
		}
	}

	/**
	 * API GET request
	 *
	 * @param url
	 * @param body
	 * @return
	 * @throws InvalidCredentialsException
	 */
	protected async get(url: string, body: RequestBehaviour): Promise<Response> {
		try {
			if (url !== PUBLIC_KEY_PATH) {
				await this.validateEncryption(body);
			}

			const response = await this.buildRequest(url, body).get(url, {
				params: JSON.parse(body.toJson()),
			});

			return this.parseResponse(response.data, response.status);
		} catch (e) {
			return this.exceptionResponse(e as Error);
		}
	}
}
